import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { useAuth } from '~/app/auth/context/AuthContext';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { useWorkOrderBoard } from '~/app/hooks/maintenance-execution/useWorkOrderBoard';
import { useWorkOrderMutations } from '~/app/hooks/maintenance-execution/useWorkOrderMutations';
import { useWorkOrderKanbanBoardData } from './useWorkOrderKanbanBoardData';
import { useWorkOrderKanbanColumns } from './useWorkOrderKanbanColumns';
import { useWorkOrderKanbanDialogs } from './useWorkOrderKanbanDialogs';
import { useWorkOrderKanbanDrag } from './useWorkOrderKanbanDrag';
import { useWorkOrderKanbanFilters } from './useWorkOrderKanbanFilters';
import { useWorkOrderKanbanLookups } from './useWorkOrderKanbanLookups';

/**
 * Kanban page orchestrator — composes filters, board projection, dialogs, and
 * drag handlers. Keep this file thin; put domain-specific logic in the focused hooks.
 */
export function useWorkOrderKanbanPage() {
  const { t } = useTranslation('maintenance');
  const { message } = App.useApp();
  const { user } = useAuth();
  const { hasPermission } = usePermission();
  const organizationId = user?.organizationId ?? '';
  const canManage = hasPermission(PermissionKey.WORK_ORDER_MANAGE);

  const columns = useWorkOrderKanbanColumns();
  const boardQuery = useWorkOrderBoard(organizationId);
  const mutations = useWorkOrderMutations(organizationId);
  const lookups = useWorkOrderKanbanLookups(organizationId);
  const filters = useWorkOrderKanbanFilters();

  const board = useWorkOrderKanbanBoardData({
    boardCards: boardQuery.data?.workOrders,
    siteNameById: lookups.siteNameById,
    technicianNameById: lookups.technicianNameById,
    t,
    siteFilter: filters.siteFilter,
    technicianFilter: filters.technicianFilter,
    planFilter: filters.planFilter,
    followUpOnly: filters.followUpOnly,
    dueRange: filters.dueRange,
    kanbanSort: filters.kanbanSort,
  });

  const dialogs = useWorkOrderKanbanDialogs({
    workOrders: board.workOrders,
    filteredWorkOrders: board.filteredWorkOrders,
    mutations,
    message,
    t,
  });

  const { onDragEnd } = useWorkOrderKanbanDrag({
    canManage,
    workOrders: board.workOrders,
    mutations,
    message,
    t,
    setPendingAssign: dialogs.setPendingAssign,
    setPendingHold: dialogs.setPendingHold,
    setPendingComplete: dialogs.setPendingComplete,
    setPendingMove: dialogs.setPendingMove,
  });

  return {
    organizationId,
    canManage,
    columns,
    columnsMap: board.columnsMap,
    workOrders: board.workOrders,
    findTicket: dialogs.findTicket,
    openMoveTicketFromBoard: dialogs.openMoveTicketFromBoard,
    isLoading: boardQuery.isLoading || lookups.isLoadingLookups,
    isError: boardQuery.isError,
    siteFilter: filters.siteFilter,
    setSiteFilter: filters.setSiteFilter,
    technicianFilter: filters.technicianFilter,
    setTechnicianFilter: filters.setTechnicianFilter,
    planFilter: filters.planFilter,
    setPlanFilter: filters.setPlanFilter,
    kanbanSort: filters.kanbanSort,
    setKanbanSort: filters.setKanbanSort,
    dueRange: filters.dueRange,
    setDueRange: filters.setDueRange,
    followUpOnly: filters.followUpOnly,
    setFollowUpOnly: filters.setFollowUpOnly,
    siteFilterOptions: lookups.siteFilterOptions,
    planFilterOptions: board.planFilterOptions,
    siteNameById: lookups.siteNameById,
    technicianOptions: lookups.technicianOptions,
    selectedWorkOrder: dialogs.selectedWorkOrder,
    selectedWorkOrderId: dialogs.selectedWorkOrderId,
    setSelectedWorkOrderId: dialogs.setSelectedWorkOrderId,
    pendingAssign: dialogs.pendingAssign,
    setPendingAssign: dialogs.setPendingAssign,
    pendingHold: dialogs.pendingHold,
    setPendingHold: dialogs.setPendingHold,
    pendingComplete: dialogs.pendingComplete,
    setPendingComplete: dialogs.setPendingComplete,
    pendingMove: dialogs.pendingMove,
    setPendingMove: dialogs.setPendingMove,
    followUpTickets: dialogs.followUpTickets,
    setFollowUpTickets: dialogs.setFollowUpTickets,
    moveTargetOptions: dialogs.moveTargetOptions,
    onDragEnd,
    confirmAssign: dialogs.confirmAssign,
    confirmHold: dialogs.confirmHold,
    confirmComplete: dialogs.confirmComplete,
    confirmMoveFromPicker: dialogs.confirmMoveFromPicker,
    openMoveForTicket: dialogs.openMoveForTicket,
    isAssigning: dialogs.isAssigning,
    isHolding: dialogs.isHolding,
    isCompleting: dialogs.isCompleting,
    isMoving: dialogs.isMoving,
  };
}
